import {
  caixaAlta,
  caixaAltaMultilinha,
  comporCstComOrigem,
  descreverModalidadeFrete,
  descreverTipoOperacao,
  formatarAliquota,
  formatarChaveEmBlocos,
  formatarData,
  formatarDataHora,
  formatarMoeda,
  formatarNumero,
  formatarNumeroNota,
  formatarQuantidade,
  formatarSerie,
  juntar,
  mascararCep,
  mascararCnpjCpf,
  mascararTelefone,
  separarDataHora,
} from './formatadoresDanfe';

/**
 * @file ./models/mapper/danfeMapper.js
 *
 * *Mapper* da camada de infraestrutura: converte o JSON estrutural da NF-e
 * — a saída de `converterXmlNfeParaJson` do projeto NFe — num **DTO de
 * visualização** do DANFE, com todo o texto já mascarado, formatado e em
 * caixa alta (exigência transversal do MOC 7.0 – Anexo II, item 3.7).
 *
 * A separação é deliberada: o motor de renderização não conhece XML nem
 * regra fiscal, e o mapper não conhece coordenadas nem tipografia. A
 * fronteira entre os dois é exclusivamente este DTO.
 *
 * ## Regra fiscal que mora aqui
 *
 * O gerador de origem resolvia no layout o que este mapper resolve na
 * entrada: escolher o CST entre os grupos `ICMS00`…`ICMSSN900`, zerar
 * base/valor/alíquota nos CSTs sem base de cálculo e ler o IPI só de
 * `IPITrib`. O `converterXmlNfeParaJson` já entrega o CST/CSOSN unificado e
 * zeros nos campos ausentes, então essa lógica desaparece — o layout
 * recebe strings prontas.
 *
 * ## Campos ainda não fornecidos por `converterXmlNfeParaJson`
 *
 * Os grupos `cobr`, `transp/transporta`, `transp/veicTransp`, `transp/vol`
 * e `det/infAdProd` não são extraídos pelo conversor atual. O mapper os lê
 * de forma opcional: estender o conversor faz os quadros preencherem sem
 * tocar no template. Enquanto isso, saem em branco.
 */

/** `tpAmb` = 2 — exige a tarja "SEM VALOR FISCAL" (MOC 3.9). */
const AMBIENTE_HOMOLOGACAO = '2';

/**
 * `tpEmis` = 7 — SVC-RS. Único tipo de contingência aceito por este
 * emissor; qualquer outro valor (2, 3, 4, 5, 6, 9) é tratado como não
 * usado aqui, então a checagem é direta em vez de uma tabela de códigos.
 */
const TIPO_EMISSAO_CONTINGENCIA = '7';

/**
 * Texto que a SEFAZ obriga na razão social do destinatário em homologação,
 * e que o DANFE reproduz.
 */
const AVISO_HOMOLOGACAO =
  'NF-E EMITIDA EM AMBIENTE DE HOMOLOGACAO - SEM VALOR FISCAL';

function lista(valor) {
  if (valor === undefined || valor === null) return [];
  return Array.isArray(valor) ? valor : [valor];
}

/**
 * Endereço em partes cruas. Diferente da versão anterior deste mapper, o
 * número **não** é embutido no logradouro: o layout compõe
 * `logradouro, numero` no campo de endereço e usa `bairro`, `cep`,
 * `municipio` e `uf` em campos separados.
 *
 * @param {Object} endereco - Endereço no formato do conversor.
 * @returns {Object}
 */
function mapearEndereco(endereco) {
  const e = endereco ?? {};

  return {
    logradouro: caixaAlta(juntar([e.logradouro, e.complemento], ' - ')),
    numero: caixaAlta(e.numero),
    bairro: caixaAlta(e.bairro),
    cep: mascararCep(e.cep),
    municipio: caixaAlta(e.municipio),
    uf: caixaAlta(e.uf),
    telefone: mascararTelefone(e.telefone),
  };
}

/**
 * Emitente. O MOC 3.7.6 manda todo este quadro em negrito, com razão social
 * em 12 pt e os demais dados em 8 pt — o peso e os corpos ficam por conta
 * da grade; aqui só o conteúdo.
 *
 * @param {Object} emitente
 * @returns {Object}
 */
function mapearEmitente(emitente) {
  const e = emitente ?? {};

  return {
    nome: caixaAlta(e.nome),
    fantasia: caixaAlta(e.fantasia),
    documento: mascararCnpjCpf(e.cnpj || e.cpf),
    inscricaoEstadual: caixaAlta(e.inscricaoEstadual),
    inscricaoEstadualSubstituto: caixaAlta(
      e.inscricaoEstadualSubstitutoTributario,
    ),
    inscricaoMunicipal: caixaAlta(e.inscricaoMunicipal),
    endereco: mapearEndereco(e.endereco),
  };
}

/**
 * Destinatário/remetente. `indicadorInscricaoEstadual` = 2 significa
 * contribuinte isento — o DANFE imprime "ISENTO" no lugar do número.
 *
 * Em homologação a razão social é substituída pelo aviso obrigatório, como
 * consta do próprio XML de teste.
 *
 * @param {Object} destinatario
 * @param {boolean} homologacao
 * @returns {Object}
 */
function mapearDestinatario(destinatario, homologacao) {
  const d = destinatario ?? {};
  const isento = String(d.indicadorInscricaoEstadual) === '2';

  return {
    nome: homologacao ? AVISO_HOMOLOGACAO : caixaAlta(d.nome),
    documento: mascararCnpjCpf(d.cnpj || d.cpf || d.idEstrangeiro),
    inscricaoEstadual: isento ? 'ISENTO' : caixaAlta(d.inscricaoEstadual),
    endereco: mapearEndereco(d.endereco),
  };
}

/**
 * Duplicatas do grupo `cobr`. Governam a altura do quadro de fatura e, por
 * consequência, a posição de tudo o que vem depois — ver `paginacao.js`.
 *
 * @param {Object} dadosNfe
 * @returns {{ duplicatas: Array<Object> }}
 */
function mapearCobranca(dadosNfe) {
  const duplicatas = lista(dadosNfe?.cobranca?.duplicatas).map((duplicata) => ({
    numero: caixaAlta(duplicata?.numero),
    vencimento: formatarData(duplicata?.vencimento),
    valor: formatarMoeda(duplicata?.valor),
  }));

  return { duplicatas };
}

/**
 * Peso de volume: 3 casas decimais, como no layout 4.00 (`pesoB`/`pesoL`).
 *
 * @param {number|string} valor
 * @returns {string}
 */
function formatarPeso(valor) {
  const numero = Number(valor);
  if (!Number.isFinite(numero) || numero === 0) return '';
  return formatarNumero(numero, 3);
}

/**
 * Quadro "Transportador / Volumes Transportados".
 *
 * @param {Object} transporte
 * @returns {Object}
 */
function mapearTransporte(transporte) {
  const t = transporte ?? {};
  const transportadora = t.transportadora ?? {};
  const veiculo = t.veiculo ?? {};
  const endereco = mapearEndereco(transportadora.endereco);

  const volumes = lista(t.volumes).map((volume) => ({
    quantidade: volume?.quantidade ? String(volume.quantidade) : '',
    especie: caixaAlta(volume?.especie),
    marca: caixaAlta(volume?.marca),
    numeracao: caixaAlta(volume?.numeracao),
    pesoBruto: formatarPeso(volume?.pesoBruto),
    pesoLiquido: formatarPeso(volume?.pesoLiquido),
  }));

  // Sem o grupo `vol` detalhado, ao menos a quantidade total de volumes
  // que o conversor já extrai é impressa — o quadro é obrigatório.
  if (volumes.length === 0 && t.quantidadeVolumes) {
    volumes.push({
      quantidade: String(t.quantidadeVolumes),
      especie: '',
      marca: '',
      numeracao: '',
      pesoBruto: '',
      pesoLiquido: '',
    });
  }

  return {
    modalidadeFrete: descreverModalidadeFrete(t.modalidadeFrete),
    transportadora: {
      nome: caixaAlta(transportadora.nome),
      documento: mascararCnpjCpf(transportadora.cnpj || transportadora.cpf),
      inscricaoEstadual: caixaAlta(transportadora.inscricaoEstadual),
      endereco: endereco.logradouro,
      municipio: endereco.municipio,
      uf: endereco.uf,
    },
    veiculo: {
      placa: caixaAlta(veiculo.placa),
      uf: caixaAlta(veiculo.uf),
      codigoAntt: caixaAlta(veiculo.codigoAntt ?? t.codigoAntt),
    },
    volumes,
  };
}

/**
 * Linha do quadro "Dados dos Produtos/Serviços".
 *
 * `descricaoCompleta` junta a descrição do produto com as informações
 * adicionais do item (`infAdProd`), que o MOC 3.1.7 manda imprimir logo
 * abaixo do item a que se referem — é também o texto que o motor de
 * paginação mede para decidir a altura da linha.
 *
 * @param {Object} item
 * @returns {Object}
 */
function mapearItem(item) {
  const i = item ?? {};
  const icms = i.icms ?? {};
  const ipi = i.ipi ?? {};

  const descricao = caixaAlta(i.descricao);
  const complementar = caixaAlta(i.informacoesAdicionais);

  return {
    numero: String(i.numero ?? ''),
    codigo: caixaAlta(i.codigo),
    descricaoCompleta: complementar
      ? `${descricao}\n${complementar}`
      : descricao,
    ncm: caixaAlta(i.ncm),
    cst: comporCstComOrigem(icms.origem, icms.cst),
    cfop: caixaAlta(i.cfop),
    unidade: caixaAlta(i.unidadeComercial),
    quantidade: formatarQuantidade(i.quantidadeComercial),
    valorUnitario: formatarMoeda(i.valorUnitarioComercial),
    valorDesconto: formatarMoeda(i.valorDesconto),
    valorTotal: formatarMoeda(i.valorTotalProduto),
    baseCalculoIcms: formatarMoeda(icms.baseCalculo),
    baseCalculoIcmsSt: formatarMoeda(icms.baseCalculoSt),
    valorIcms: formatarMoeda(icms.valor),
    valorIcmsSt: formatarMoeda(icms.valorSt),
    valorIpi: formatarMoeda(ipi.valor),
    aliquotaIcms: formatarAliquota(icms.aliquota),
    aliquotaIpi: formatarAliquota(ipi.aliquota),
  };
}

/**
 * Quadro "Cálculo do Imposto".
 *
 * @param {Object} totais
 * @returns {Object}
 */
function mapearImpostos(totais) {
  const t = totais ?? {};
  const icms = t.icms ?? {};

  return {
    baseCalculoIcms: formatarMoeda(icms.baseCalculo),
    valorIcms: formatarMoeda(icms.valor),
    baseCalculoIcmsSt: formatarMoeda(icms.baseCalculoSt),
    valorIcmsSt: formatarMoeda(icms.valorSt),
    valorAproximadoTributos: formatarMoeda(t.valorAproximadoTributos),
    valorProdutos: formatarMoeda(t.valorProdutos),
    valorFrete: formatarMoeda(t.valorFrete),
    valorSeguro: formatarMoeda(t.valorSeguro),
    valorDesconto: formatarMoeda(t.valorDesconto),
    valorOutrasDespesas: formatarMoeda(t.valorOutrasDespesas),
    valorIpi: formatarMoeda(t.valorIpi),
    valorPis: formatarMoeda(t.valorPis),
    valorCofins: formatarMoeda(t.valorCofins),
    valorTotalNota: formatarMoeda(t.valorTotalNota),
  };
}

/**
 * Compõe o campo "Informações Complementares".
 *
 * O MOC 3.1.8 exige que ele contenha `infAdFisco` e `infCpl`, facultando
 * `obsCont`. O item 3.10.5 acrescenta que campos sem lugar próprio no
 * leiaute — o `vICMSDeson` é o caso citado — devem ser copiados para cá.
 *
 * @param {Object} dadosNfe
 * @returns {string}
 */
function comporInformacoesComplementares(dadosNfe) {
  const infAdic = dadosNfe?.informacoesAdicionais ?? {};
  const partes = [];

  if (infAdic.informacoesComplementares) {
    const textoFormatado = caixaAltaMultilinha(
      infAdic.informacoesComplementares,
    );
    if (textoFormatado) {
      partes.push(textoFormatado);
    }
  }

  lista(infAdic.observacoes)
    .filter((observacao) => {
      const campo = String(observacao?.campo ?? '').trim().toLowerCase();
      return (
        campo !== 'enviaemail' &&
        campo !== 'envia_email' &&
        campo !== 'email'
      );
    })
    .forEach((observacao) => {
      const texto = juntar([observacao?.campo, observacao?.texto], ': ');
      if (texto) partes.push(caixaAlta(texto));
    });

  const desonerado = Number(dadosNfe?.totais?.icms?.valorDesonerado ?? 0);
  if (desonerado > 0) {
    partes.push(`VALOR DO ICMS DESONERADO: R$ ${formatarMoeda(desonerado)}.`);
  }

  const troco = Number(dadosNfe?.pagamento?.troco ?? 0);
  if (troco > 0) {
    partes.push(`TROCO: R$ ${formatarMoeda(troco)}.`);
  }

  return partes.join('\n');
}

/**
 * Converte o JSON estrutural da NF-e no DTO de visualização do DANFE.
 *
 * @param {Object} dadosNfe - `dadosNfe` devolvido por `converterXmlNfeParaJson`.
 * @returns {Object} DTO consumido por `danfeProducao`.
 * @throws {TypeError} Se `dadosNfe` não for um objeto.
 */
export function mapearDanfe(dadosNfe) {
  if (!dadosNfe || typeof dadosNfe !== 'object') {
    throw new TypeError(
      '[mapearDanfe] O parâmetro "dadosNfe" é obrigatório e deve ser o objeto devolvido por converterXmlNfeParaJson.',
    );
  }

  const identificacao = dadosNfe.identificacao ?? {};
  const protocolo = dadosNfe.protocolo ?? {};
  const homologacao =
    String(identificacao.tipoAmbiente) === AMBIENTE_HOMOLOGACAO;
  const emissao = separarDataHora(identificacao.dataEmissao);
  const saidaEntrada = separarDataHora(identificacao.dataSaidaEntrada);

  return {
    ambiente: {
      homologacao,
      aviso: homologacao ? AVISO_HOMOLOGACAO : '',
    },
    documento: {
      numero: formatarNumeroNota(identificacao.numero),
      serie: formatarSerie(identificacao.serie),
      modelo: String(identificacao.modelo ?? '55'),
      naturezaOperacao: caixaAlta(identificacao.naturezaOperacao),
      tipoOperacao: descreverTipoOperacao(identificacao.tipoOperacao),
      dataEmissao: emissao.data,
      dataSaidaEntrada: saidaEntrada.data,
      horaSaidaEntrada: saidaEntrada.hora,
      /** Governa a tarja "NF-E NÃO ENVIADA PARA SEFAZ" e o bloco de consulta. */
      autorizada: Boolean(protocolo.numero),
      protocolo: protocolo.numero
        ? `${protocolo.numero} - ${formatarDataHora(protocolo.dataRecebimento)}`
        : '',
      /** MOC 3.9.2 — substitui o bloco de consulta pelo aviso de contingência. */
      contingencia:
        String(identificacao.tipoEmissao) === TIPO_EMISSAO_CONTINGENCIA,
    },
    chave: {
      valor: String(dadosNfe.chaveAcesso ?? '').replace(/\D/g, ''),
      formatada: formatarChaveEmBlocos(dadosNfe.chaveAcesso),
    },
    emitente: mapearEmitente(dadosNfe.emitente),
    destinatario: mapearDestinatario(dadosNfe.destinatario, homologacao),
    cobranca: mapearCobranca(dadosNfe),
    impostos: mapearImpostos(dadosNfe.totais),
    transporte: mapearTransporte(dadosNfe.transporte),
    itens: lista(dadosNfe.itens).map(mapearItem),
    dadosAdicionais: {
      informacoesComplementares: comporInformacoesComplementares(dadosNfe),
      reservadoFisco: caixaAlta(
        dadosNfe?.informacoesAdicionais?.informacoesFisco,
      ),
    },
  };
}

export default mapearDanfe;
